import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import type { ArtifactView as ArtifactViewSchema } from "@/types/artifacts-view.ts";
import type { ClaimedArtifactResp } from "@/types/sharedArtifact.ts";

/**
 * Someone else's artifact, reached through a share grant. Read-only, with no
 * footer and no composer: a grant conveys reading, and everything those act on
 * is a thread the viewer has no access to.
 *
 * One component for every surface a grantee can land on — the artifact page
 * and the canvas — so they cannot disagree about what a grantee may touch.
 */
export const ClaimedArtifactView = ({
  artifact,
  view,
  userIsAdmin,
  onRequestClose,
}: {
  artifact: ClaimedArtifactResp;
  view: ArtifactViewSchema;
  userIsAdmin: boolean;
  onRequestClose: () => void;
}) => (
  <ArtifactErrorBoundary key={artifact.id} artifact={artifact} userIsAdmin={userIsAdmin}>
    {/* Optional: a client this new can reach a backend that doesn't send it
        yet, and an unattributed page beats an empty attribution line. */}
    {artifact.sharedBy && (
      <p className="border-b px-5 py-3 text-sm text-muted-foreground">
        Shared with you by {artifact.sharedBy.name}
      </p>
    )}
    <ArtifactView
      view={view}
      threadId={-1}
      messageId={-1}
      artifactId={artifact.id}
      isReadOnly
      // `isReadOnly` alone is not enough. It suppresses editing; it does not
      // suppress the blocks whose *reads* are thread-scoped — document
      // export, image previews, audio, auth-only links all key off
      // `isSharedThread` instead. A claimant has no more thread access than
      // an anonymous viewer, so they take the same branch, and the sentinel
      // ids never reach a `/api/a/threads/-1/...` request.
      isSharedThread
      appletPreviewSrc={resolveApiPath(`/api/a/artifacts/${artifact.id}/appletPreview`)}
      maximized
      onRequestClose={onRequestClose}
      patcherOverride={READ_ONLY_PATCHER}
    />
  </ArtifactErrorBoundary>
);

/**
 * A claimed artifact has no thread to patch against, and the default patcher
 * POSTs to a thread-scoped route. Overriding it means a block that ignores
 * `isReadOnly` still cannot issue a write.
 */
const READ_ONLY_PATCHER = {
  patch: async () => undefined,
  patchOne: async () => undefined,
  isPending: false,
};
